import { useEffect, useState } from "react";
import { CarSpendDb } from "../../core/index.ts";

// Same "carspend" IndexedDB as useCarspend's instance — blobs written here are
// visible to the export path (Dexie instances share the underlying database).
const attachDb = new CarSpendDb();

const labelStyle: React.CSSProperties = {
  fontFamily: "var(--mono)", fontSize: 10, letterSpacing: 1.4, color: "var(--muted)",
  textTransform: "uppercase", marginBottom: 5, display: "block",
};
const addTile: React.CSSProperties = {
  width: 72, height: 72, borderRadius: 8, border: "1px dashed var(--amberDim)",
  display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
  color: "var(--amber)", fontSize: 22,
};
const addRow: React.CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: "pointer",
  border: "1px dashed var(--amberDim)", borderRadius: 8, padding: "10px 12px",
  color: "var(--amber)", fontFamily: "var(--mono)", fontSize: 11, letterSpacing: 1.2,
};
const removeBtn: React.CSSProperties = {
  border: "none", background: "var(--obsidian)", color: "var(--redline)",
  cursor: "pointer", borderRadius: 9, width: 18, height: 18, fontSize: 12, lineHeight: "18px", padding: 0,
};

function extOf(name: string): string {
  const e = (name.split(".").pop() ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
  return e.length >= 1 && e.length <= 5 ? e : "bin";
}

async function saveBlob(file: File): Promise<string> {
  const filename = `${crypto.randomUUID()}.${extOf(file.name)}`;
  await attachDb.putAttachment(filename, file);
  return filename;
}

/** Load a stored blob → object URL. `missing` is true once we've looked and found
 *  no blob (a dangling reference — filename in the record but no file in the store). */
function useObjectURL(filename: string | null | undefined): { url: string | null; missing: boolean } {
  const [state, setState] = useState<{ url: string | null; missing: boolean }>({ url: null, missing: false });
  useEffect(() => {
    if (!filename) { setState({ url: null, missing: false }); return; }
    let created: string | null = null;
    let active = true;
    void attachDb.getAttachment(filename).then((blob) => {
      if (!active) return;
      if (blob) { created = URL.createObjectURL(blob); setState({ url: created, missing: false }); }
      else setState({ url: null, missing: true });
    });
    return () => { active = false; if (created) URL.revokeObjectURL(created); };
  }, [filename]);
  return state;
}

function Thumb({ filename, onRemove }: { filename: string; onRemove: () => void }) {
  const { url, missing } = useObjectURL(filename);
  return (
    <div style={{ position: "relative", width: 72, height: 72, borderRadius: 8, overflow: "hidden", border: "1px solid var(--edge)", background: "var(--panel)" }}>
      {url
        ? <a href={url} target="_blank" rel="noreferrer" title="View full size" style={{ display: "block", width: "100%", height: "100%" }}>
            <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "pointer" }} />
          </a>
        : <div className="mono muted" style={{ fontSize: 8, textAlign: "center", padding: 4, display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>{missing ? "no file in backup" : "…"}</div>}
      <button type="button" onClick={onRemove} aria-label="Remove photo" style={{ ...removeBtn, position: "absolute", top: 2, right: 2 }}>×</button>
    </div>
  );
}

function DocRow({ filename, onRemove }: { filename: string; onRemove: () => void }) {
  const { url, missing } = useObjectURL(filename);
  const ext = (filename.split(".").pop() ?? "").toUpperCase();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid var(--edge)", borderRadius: 8, padding: "8px 10px", background: "var(--panel)" }}>
      <span className="amber" style={{ fontSize: 14 }}>📄</span>
      {url
        ? <a href={url} target="_blank" rel="noreferrer" className="mono cream" style={{ fontSize: 12, flex: 1, textDecoration: "none" }}>Attached · {ext} — open</a>
        : <span className="mono muted" style={{ fontSize: 12, flex: 1 }}>{ext} · {missing ? "no file in backup" : "loading…"}</span>}
      <button type="button" onClick={onRemove} aria-label="Remove document" style={removeBtn}>×</button>
    </div>
  );
}

/** A grid of photo thumbnails with add/remove. */
export function PhotoField({ label = "Photos", filenames, onChange }: {
  label?: string; filenames: string[]; onChange: (next: string[]) => void;
}) {
  async function add(files: FileList | null) {
    if (!files || files.length === 0) return;
    const added: string[] = [];
    for (const f of Array.from(files)) added.push(await saveBlob(f));
    onChange([...filenames, ...added]);
  }
  async function remove(name: string) {
    await attachDb.deleteAttachment(name);
    onChange(filenames.filter((n) => n !== name));
  }
  return (
    <div style={{ marginBottom: 12 }}>
      <span style={labelStyle}>{label}</span>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {filenames.map((n) => <Thumb key={n} filename={n} onRemove={() => void remove(n)} />)}
        <label style={addTile}>
          +
          <input type="file" accept="image/*" multiple hidden onChange={(e) => { void add(e.target.files); e.target.value = ""; }} />
        </label>
      </div>
    </div>
  );
}

/** A list of attached documents (PDF/image) with add/remove. */
export function DocField({ label = "Documents", filenames, onChange }: {
  label?: string; filenames: string[]; onChange: (next: string[]) => void;
}) {
  async function add(files: FileList | null) {
    if (!files || files.length === 0) return;
    const added: string[] = [];
    for (const f of Array.from(files)) added.push(await saveBlob(f));
    onChange([...filenames, ...added]);
  }
  async function remove(name: string) {
    await attachDb.deleteAttachment(name);
    onChange(filenames.filter((n) => n !== name));
  }
  return (
    <div style={{ marginBottom: 12 }}>
      <span style={labelStyle}>{label}</span>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {filenames.map((n) => <DocRow key={n} filename={n} onRemove={() => void remove(n)} />)}
        <label style={addRow}>
          + ADD DOCUMENT
          <input type="file" accept="application/pdf,image/*" multiple hidden onChange={(e) => { void add(e.target.files); e.target.value = ""; }} />
        </label>
      </div>
    </div>
  );
}

/** A single document slot (e.g. insurance / road-tax); replaces on re-pick. */
export function SingleDocField({ label, filename, onChange }: {
  label: string; filename: string | null | undefined; onChange: (next: string | null) => void;
}) {
  async function pick(files: FileList | null) {
    if (!files || files.length === 0) return;
    if (filename) await attachDb.deleteAttachment(filename);
    onChange(await saveBlob(files[0]!));
  }
  async function remove() {
    if (filename) await attachDb.deleteAttachment(filename);
    onChange(null);
  }
  return (
    <div style={{ marginBottom: 12 }}>
      <span style={labelStyle}>{label}</span>
      {filename
        ? <DocRow filename={filename} onRemove={() => void remove()} />
        : (
          <label style={addRow}>
            + ATTACH
            <input type="file" accept="application/pdf,image/*" hidden onChange={(e) => { void pick(e.target.files); e.target.value = ""; }} />
          </label>
        )}
    </div>
  );
}
